import { useClientPortalAPI, useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text } from "@certn/cascadia";
import {
  Container,
  Layout,
  Loader,
  SecondaryTopNav,
  SelectFormikField,
  SelectItem,
  TextAreaFormikField,
  PrimaryButton,
  AvatarWithName,
  Divider,
  FormLabelRAC,
  DialogTrigger,
  LinkButton,
  Popover,
  PopoverContent,
  ActionListItem,
  TextInputFormikField,
  TextField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { array, object, string } from "yup";

import { applyLinkPagePermissions } from "./components/ActionsDropdown";
import { ClientJsonForm } from "../../components/client-json-form/ClientJsonForm";
import { CopyLinkButton } from "../../components/copy-link-button/CopyLinkButton";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { getJsonFormPayload } from "../../utils/getJsonFormPayload";

export function ApplyLinkDetails() {
  const { t } = useTranslation();
  const { applyLinkId } = useParams();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [validateJsonForm, setValidateJsonForm] = useState(false);
  const { hasPermission } = usePermissions();
  const canEditApplyLink = hasPermission(applyLinkPagePermissions);

  const {
    data: applyLinkData,
    mutate,
    isLoading,
  } = useClientPortalAPI(
    `/api/internal/client-portal/apply-links/${applyLinkId}/`
  );

  const { data: groups = [], isLoading: isGroupsLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups/`
  );
  const { data: packages, isLoading: isPackagesLoading } = useClientPortalAPI(
    selectedGroup
      ? `/api/internal/client-portal/packages/ordereable-packages/customer-group/${selectedGroup}/`
      : null
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/apply-links/${applyLinkId}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: (response: components["schemas"]["ApplyLink"]) => {
        navigate(`/apply-links`);
        mutate();
      },
    }
  );

  const formSchema = object().shape({
    name: string().min(3).required().label(t("Apply link name")).trim(),
    customer_group: string().required().label(t("Group")),
    package: string().required().label(t("Package")),
    additional_message: string().label(t("Additional message")),
    // The client json form will validate its own data. This is just to block submitting the form.
    clientFormDataAndErrors: object()
      .shape({
        errors: array().length(0),
      })
      .required(),
  });

  useEffect(() => {
    if (applyLinkData) {
      setSelectedGroup(applyLinkData?.customer_group.id);
      setSelectedPackage(applyLinkData?.package.id);
    }
  }, [applyLinkData]);

  return (
    <Formik
      initialValues={{
        name: "",
        customer_group: "",
        package: "",
        additional_message: "",
        clientFormDataAndErrors: {
          data: applyLinkData?.client_form_data,
          errors: [],
        },
      }}
      onSubmit={(values) =>
        submit({
          ...values,
          is_active: applyLinkData?.is_active,
          client_form_data: values.clientFormDataAndErrors.data,
        })
      }
      validationSchema={formSchema}
    >
      {({
        handleSubmit,
        isSubmitting,
        isValid,
        values,
        setFieldValue,
        setTouched,
      }) => {
        // Other solutions, including initialValues, do not set the field value of customer_group, so this is needed.
        if (!values.customer_group && selectedGroup) {
          setFieldValue("customer_group", selectedGroup);
        }
        if (!values.additional_message && applyLinkData?.additional_message) {
          setFieldValue(
            "additional_message",
            applyLinkData?.additional_message
          );
        }
        if (!values.name && applyLinkData?.name) {
          setFieldValue("name", applyLinkData?.name);
        }
        if (!values.package && selectedPackage) {
          setFieldValue("package", selectedPackage);
        }

        return (
          <Layout
            header={
              <SecondaryTopNav
                testID="apply-link-details-top-nav"
                leftIcon="close"
                onleftIconOnPress={() => navigate("/apply-links")}
              >
                <PrimaryButton
                  onPress={() => {
                    setValidateJsonForm(true);
                    setTouched(
                      Object.keys(values).reduce((acc, key) => {
                        if (key === "clientFormDataAndErrors") return acc;
                        return {
                          ...acc,
                          [key]: true,
                        };
                      }, {})
                    );
                    if (isValid) handleSubmit();
                  }}
                  isLoading={isSubmitting}
                  testID="save-changes-button"
                >
                  <Trans>Save changes</Trans>
                </PrimaryButton>
              </SecondaryTopNav>
            }
          >
            <Container
              padding={["space-3"]}
              layout="narrow"
              testID="apply-link-details-container"
            >
              {!isLoading ? (
                <div className={styles.pageLayout}>
                  <Text
                    as="h1"
                    style="global/title-small"
                    testID="apply-link-title"
                  >
                    {t("Apply link")}
                  </Text>
                  <div className={styles.applyLinkDetailsArea}>
                    <Text
                      testID="apply-link-details-heading"
                      style="global/heading-medium"
                    >
                      {t("Apply link details")}
                    </Text>
                    <TextInputFormikField
                      name="name"
                      label={t("Apply link name")}
                      isRequired
                      description={t(
                        "Used internally to identify the apply link. The applicant will not see this."
                      )}
                      testID="apply-link-name"
                      isDisabled={!canEditApplyLink}
                    />
                    <div
                      className={styles.requestor}
                      data-testid="apply-link-requestor"
                    >
                      <FormLabelRAC
                        isRequired
                        info={
                          <DialogTrigger>
                            <LinkButton
                              testID="requestor-info"
                              textStyle="form/body-secondary-link"
                            >
                              <Trans>What's this?</Trans>
                            </LinkButton>
                            <Popover placement="top right">
                              <PopoverContent>
                                {t(
                                  "All applicants who use this link will have their cases automatically assigned to the requestor."
                                )}
                              </PopoverContent>
                            </Popover>
                          </DialogTrigger>
                        }
                      >
                        <Trans>Requestor</Trans>
                      </FormLabelRAC>
                      <ActionListItem
                        padding="var(--space-3) 0"
                        title={
                          <AvatarWithName
                            name={applyLinkData?.requestor?.auth0_full_name}
                            textStyle="global/heading-medium"
                            testID="requestor"
                          />
                        }
                      />
                    </div>
                    <div
                      className={
                        (styles.copyURL,
                        "client-portal--apply-link-page--copy-apply-link-click-rate")
                      }
                    >
                      <TextField
                        label={t("Apply link URL")}
                        aria-label={t("Apply link URL")}
                        testID="apply-link-url-field"
                        isDisabled
                        isRequired
                        value={applyLinkData?.link}
                        style={{ width: "445px" }}
                      />
                      <CopyLinkButton url={applyLinkData?.link || ""} />
                    </div>
                  </div>
                  <Divider />
                  <div className={styles.caseDetailsArea}>
                    <Text
                      testID="case-details-heading"
                      style="global/heading-medium"
                    >
                      {t("Case details")}
                    </Text>
                    <SelectFormikField
                      name="customer_group"
                      label={t("Group")}
                      isRequired
                      isLoading={isGroupsLoading}
                      onSelectionChange={(group) => {
                        setSelectedGroup(group?.toString() ?? "");
                      }}
                      testID="apply-link-group"
                      selectedKey={selectedGroup}
                      isDisabled={!canEditApplyLink}
                    >
                      {groups?.map((group) => (
                        <SelectItem id={group.id} key={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectFormikField>
                    <SelectFormikField
                      name="package"
                      label={t("Package")}
                      isRequired
                      isLoading={isPackagesLoading}
                      onSelectionChange={(packageItem) => {
                        setSelectedPackage(packageItem?.toString() ?? "");
                      }}
                      disabledKeys={packages?.flatMap((packageItem) =>
                        !packageItem.can_order ? [packageItem.id] : []
                      )}
                      isDisabled={
                        !canEditApplyLink ||
                        (canEditApplyLink && !selectedGroup)
                      }
                      testID="apply-link-package"
                      selectedKey={selectedPackage}
                    >
                      {packages?.map((packageItem) => (
                        <SelectItem id={packageItem.id} key={packageItem.id}>
                          {packageItem.name}
                        </SelectItem>
                      ))}
                    </SelectFormikField>
                    <TextAreaFormikField
                      label={t("Additional message")}
                      name="additional_message"
                      testID="apply-link-additional-message"
                      isDisabled={!canEditApplyLink}
                      maxLength={1200}
                      description={t(
                        "This message will be seen by the applicant."
                      )}
                    />
                  </div>

                  {selectedGroup &&
                    selectedPackage &&
                    canEditApplyLink &&
                    !isPackagesLoading && (
                      <ClientJsonForm
                        key={selectedPackage}
                        isSubmitted={validateJsonForm}
                        clientFormPayload={getJsonFormPayload(
                          packages,
                          selectedPackage
                        )}
                        questionsUrl={`/api/internal/client-portal/customer-groups/${selectedGroup}/order-questions/`}
                        initialData={applyLinkData?.client_form_data}
                        isApplyLink
                      />
                    )}
                </div>
              ) : (
                <Loader type="dots" />
              )}
            </Container>
          </Layout>
        );
      }}
    </Formik>
  );
}

const styles = {
  pageLayout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  applyLinkDetailsArea: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  }),
  caseDetailsArea: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  }),
  copyURL: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "100%",
    gap: "8px",
  }),
  requestor: css({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  }),
};
